import { createClient } from '@/lib/supabase/client';

const APP_URL = process.env.NEXT_PUBLIC_SITE_URL || 'https://quotedesk6033.builtwithrocket.new';

async function invokeEmailFunction(type: string, to: string, data: Record<string, any>) {
  try {
    const supabase = createClient();
    const { error } = await supabase.functions.invoke('send-email', {
      body: { type, to, data },
    });
    if (error) {
      console.warn('[EmailService] Failed to send email:', error.message);
    }
  } catch (err) {
    console.warn('[EmailService] Email invocation error:', err);
  }
}

// Notify supervisor(s) when a new purchase request needs approval
export async function notifyPurchaseRequestApproval(params: {
  supervisorEmail: string;
  supervisorName: string;
  requesterName: string;
  quoteNumber: string;
  clientName: string;
  currency: string;
  priority: string;
  itemCount: number;
  requestDate: string;
}) {
  await invokeEmailFunction('purchase_request_approval', params.supervisorEmail, {
    supervisorName: params.supervisorName,
    requesterName: params.requesterName,
    quoteNumber: params.quoteNumber,
    clientName: params.clientName,
    currency: params.currency,
    priority: params.priority,
    itemCount: params.itemCount,
    requestDate: params.requestDate,
    appUrl: APP_URL,
  });
}

// Notify salesperson when their quote is approved
export async function notifyQuoteApproved(params: {
  vendedorEmail: string;
  vendedorName: string;
  quoteNumber: string;
  clientName: string;
  totalEstimated: string;
  supervisorName: string;
  approvalNote: string;
}) {
  await invokeEmailFunction('quote_approved', params.vendedorEmail, {
    vendedorName: params.vendedorName,
    quoteNumber: params.quoteNumber,
    clientName: params.clientName,
    totalEstimated: params.totalEstimated,
    supervisorName: params.supervisorName,
    approvalNote: params.approvalNote,
    appUrl: APP_URL,
  });
}

// Notify salesperson when their quote is rejected
export async function notifyQuoteRejected(params: {
  vendedorEmail: string;
  vendedorName: string;
  quoteNumber: string;
  clientName: string;
  supervisorName: string;
  rejectionNote: string;
}) {
  await invokeEmailFunction('quote_rejected', params.vendedorEmail, {
    vendedorName: params.vendedorName,
    quoteNumber: params.quoteNumber,
    clientName: params.clientName,
    supervisorName: params.supervisorName,
    rejectionNote: params.rejectionNote,
    appUrl: APP_URL,
  });
}

// Notify relevant users when a PO is generated
export async function notifyPurchaseOrderGenerated(params: {
  recipientEmail: string;
  recipientName: string;
  poNumber: string;
  quoteNumber: string;
  clientName: string;
  vendedor: string;
  totalCost: string;
  itemCount: number;
  requestDate: string;
}) {
  await invokeEmailFunction('purchase_order_generated', params.recipientEmail, {
    recipientName: params.recipientName,
    poNumber: params.poNumber,
    quoteNumber: params.quoteNumber,
    clientName: params.clientName,
    vendedor: params.vendedor,
    totalCost: params.totalCost,
    itemCount: params.itemCount,
    requestDate: params.requestDate,
    appUrl: APP_URL,
  });
}

// Send password reset email
export async function sendPasswordResetEmail(params: {
  userEmail: string;
  resetLink: string;
}) {
  await invokeEmailFunction('password_reset', params.userEmail, {
    userEmail: params.userEmail,
    resetLink: params.resetLink,
  });
}

// Helper: fetch supervisor emails from user_profiles
export async function getSupervisorEmails(): Promise<Array<{ email: string; fullName: string }>> {
  try {
    const supabase = createClient();
    const { data } = await supabase
      .from('user_profiles')
      .select('email, full_name')
      .in('role', ['supervisor', 'administrador'])
      .eq('is_active', true);
    return (data || []).map((u: any) => ({ email: u.email, fullName: u.full_name }));
  } catch {
    return [];
  }
}

// Helper: fetch vendedor email by name
export async function getVendedorEmail(vendedorName: string): Promise<{ email: string; fullName: string } | null> {
  try {
    const supabase = createClient();
    const { data } = await supabase
      .from('user_profiles')
      .select('email, full_name')
      .ilike('full_name', `%${vendedorName}%`)
      .eq('is_active', true)
      .limit(1)
      .single();
    if (!data) return null;
    return { email: data.email, fullName: data.full_name };
  } catch {
    return null;
  }
}

// Send share access email from Quote Detail
export async function invokeShareEmail(params: {
  recipientEmail: string;
  quoteCode: string;
  quoteId: string;
  clientName: string;
  sellerName: string;
  shareLink: string;
  expiryHours: string;
  permission: 'view' | 'edit';
}) {
  await invokeEmailFunction('quote_share', params.recipientEmail, {
    quoteCode: params.quoteCode,
    quoteId: params.quoteId,
    clientName: params.clientName,
    sellerName: params.sellerName,
    shareLink: params.shareLink,
    expiryHours: params.expiryHours,
    permission: params.permission === 'edit' ? 'Edición' : 'Solo lectura',
    appUrl: APP_URL,
  });
}
